/* ==========================================================================
   ATB PDP GALLERY — enhancement layer for the product media gallery.
   Pairs with atb-pdp-gallery.css. No dependencies; progressive: if this
   file never runs, the stock Dawn gallery is untouched.

   Does three things per <media-gallery>:
   1. Marks each media container `.atb-img-loaded` when its image finishes
      loading (CSS fades the image in).
   2. Updates the server-rendered glass counter pill ("3 / 20") on swipe.
   3. Scroll-driven emphasis: per-frame CSS vars for scale / dim of each
      slide relative to the slider center (mobile scroll-snap).
   ========================================================================== */

(function () {
  'use strict';

  var REDUCE = false;
  try {
    REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* noop */ }

  function markLoaded(container) {
    var img = container.querySelector('.product__media img, .deferred-media__poster img');
    if (!img) {
      container.classList.add('atb-img-loaded');
      return;
    }
    var done = function () { container.classList.add('atb-img-loaded'); };
    if (img.complete && img.naturalWidth > 0) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }

  function enhance(gallery) {
    // 1. Blur-up for every media container (all layouts, all viewports)
    gallery.querySelectorAll('.product-media-container').forEach(markLoaded);

    var slider = gallery.querySelector('ul.product__media-list');
    if (!slider) return;
    var slides = Array.prototype.slice.call(slider.querySelectorAll('.product__media-item'));
    if (slides.length < 2) return;

    var sliderComponent = gallery.querySelector('slider-component');
    if (!sliderComponent) return;

    // 2. Counter pill (server-rendered) — we only update the number
    var counterCurrent = sliderComponent.querySelector('[data-atb-counter-current]');
    gallery.classList.add('atb-gallery-enhanced');

    // 3. Scroll-driven emphasis + dot sync (rAF-throttled, passive)
    var ticking = false;

    function update() {
      ticking = false;
      // Only meaningful when the list actually scrolls (mobile slider)
      if (slider.scrollWidth <= slider.clientWidth + 8) return;
      var rect = slider.getBoundingClientRect();
      var center = rect.left + rect.width / 2;
      var nearest = 0;
      var nearestDist = Infinity;

      slides.forEach(function (slide, i) {
        var r = slide.getBoundingClientRect();
        if (r.width === 0) return;
        var slideCenter = r.left + r.width / 2;
        var dist = slideCenter - center;
        var t = dist / r.width; // 0 = centered, ±1 = one slide away
        var a = Math.min(1, Math.abs(t));
        if (Math.abs(dist) < nearestDist) {
          nearestDist = Math.abs(dist);
          nearest = i;
        }
        if (!REDUCE) {
          slide.style.setProperty('--atb-slide-scale', (1 - 0.045 * a).toFixed(4));
          slide.style.setProperty('--atb-slide-dim', (0.22 * a).toFixed(3));
        }
      });
      if (counterCurrent) {
        var label = String(nearest + 1);
        if (counterCurrent.textContent !== label) counterCurrent.textContent = label;
      }
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    slider.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });

    // First application of the emphasis values eases in (no pop), then the
    // transition comes off so scroll-driven updates track raw per-frame.
    slides.forEach(function (s) { s.classList.add('atb-ease-in'); });
    update();
    setTimeout(function () {
      slides.forEach(function (s) { s.classList.remove('atb-ease-in'); });
    }, 400);

    // Mouse-wheel guard: Chromium can trap vertical wheel events inside an
    // x-mandatory scroll-snap container (it consumes the delta re-snapping
    // instead of chaining to the page), which reads as "the page is locked"
    // when the cursor sits over the gallery on a narrow desktop window
    // (owner repro 2026-07-03). Forward vertical wheel to the page;
    // horizontal-dominant deltas (trackpad swipes) still drive the slider.
    slider.addEventListener('wheel', function (e) {
      if (e.ctrlKey) return; // pinch-zoom gesture — leave it alone
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
      }
    }, { passive: false });
  }

  function init() {
    // Order matters: mark already-loaded images FIRST, then flip the
    // .atb-js gate — both land in the same style recalc, so a cached
    // (already sharp) image is never momentarily treated as "loading"
    // (that double-paint read as a flash on every load).
    document.querySelectorAll('media-gallery').forEach(enhance);
    document.documentElement.classList.add('atb-js');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
